import { useState } from "react";
import { Button } from "@/components/ui/button";
import { UserPlus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSelector } from "react-redux";
import { RootState } from "@/lib/store";
import { organization_type } from "@/models/organization.model";
import { useParams } from "react-router";
import { ChatMessagesRepository } from "@/repositories/chat-messages.repository";
import { ChatService } from "@/services/chat.service";
import { AssignmentModal } from "@/components/shared/assignment-modal";
import { assignAppointment } from "@/services/appointment-assignment.service";
import { assignmentChangesNothing, type ResourceAssignment } from "@/lib/appointment-assignment";
import { fieldActionMessage } from "@/lib/appointment-field-actions";
import { AppointmentsRepository } from "@/repositories/appointments.repository";
import { toast } from "sonner";

interface AssignmentTriggerMessageProps {
  messageId: string;
  appointmentId: string;
  triggeredByName: string;
  isProcessed?: boolean;
  className?: string;
}

export default function AssignmentTriggerMessage({
  messageId,
  appointmentId,
  triggeredByName,
  isProcessed = false,
  className,
}: AssignmentTriggerMessageProps) {
  const { roomId } = useParams();
  const [isHidden, setIsHidden] = useState(isProcessed);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [appointment, setAppointment] = useState<any>(null);

  const activeOrg = useSelector((s: RootState) => {
    const activeOrgId = s.organization.activeOrgId;
    return s.organization.organizations.find((org) => org.id === activeOrgId);
  });

  const actsForTheVendor = activeOrg?.type === organization_type.vendor;

  const loadAppointment = async () => {
    try {
      const appt = await AppointmentsRepository.getById(appointmentId);
      setAppointment(appt);
      return appt;
    } catch (error) {
      console.error("Failed to load appointment:", error);
      toast.error("Failed to load appointment details");
      return null;
    }
  };

  const handleOpenAssignmentModal = async () => {
    const appt = await loadAppointment();
    if (appt === null) return;
    if (appt.assigned_user?.id || appt.assigned_organization?.id) {
      toast.info("Someone is already assigned to this appointment.");
      setIsHidden(true);
      return;
    }
    setAssignmentModalOpen(true);
  };

  const handleAssignResource = async (assignment: ResourceAssignment) => {
    setIsAssigning(true);
    try {
      const changesNothing = appointment !== null && assignmentChangesNothing("vendor", assignment, appointment);
      await assignAppointment(appointmentId, assignment);
      if (changesNothing) toast.info("No changes");
      else toast.success(assignment.type === "individual" ? "Individual assigned successfully" : "Subcontractor assigned successfully");

      if (roomId) ChatService.showUpdatedMessage(roomId, await ChatMessagesRepository.markProcessed(roomId, messageId));

      setIsHidden(true);
      setAssignmentModalOpen(false);
    } catch (error) {
      console.error("Assignment failed:", error);
      toast.error(fieldActionMessage(error, "Failed to assign resource"));
      throw error;
    } finally {
      setIsAssigning(false);
    }
  };

  if (!actsForTheVendor || isHidden) {
    return null;
  }

  return (
    <>
      <div className={cn("max-w-md mx-auto my-4 border rounded-lg p-4 bg-blue-50 border-blue-200", className)}>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-1">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <UserPlus className="w-4 h-4 text-blue-600" />
            </div>
          </div>

          <div className="flex-1 space-y-3">
            <div>
              <h4 className="font-medium text-sm text-blue-900">Assignment Required</h4>
              <p className="text-sm text-blue-800 mt-1">This appointment was handed back. Assign someone to continue.</p>
            </div>

            <Button
              size="sm"
              onClick={handleOpenAssignmentModal}
              className="h-8 px-3 bg-blue-600 hover:bg-blue-700"
              disabled={isAssigning}
            >
              <UserPlus className="w-3 h-3 mr-1" />
              Assign
            </Button>
          </div>
        </div>
      </div>

      {appointment && (
        <AssignmentModal
          isOpen={assignmentModalOpen}
          onClose={() => setAssignmentModalOpen(false)}
          appointment={appointment}
          onAssign={handleAssignResource}
          isLoading={isAssigning}
        />
      )}
    </>
  );
}
